import * as Sentry from '@sentry/react'

import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router'
import { useEffect } from 'react'
import { logger } from './logger'
import { redactSecretsInText } from 'utils/logRedaction'

// Injected by Facebook/Instagram in-app browsers via WKUserContentController.addUserScript.
// Sentry attributes the stack frame to our document URL because injected scripts have none.
// Sentry issues: MIXTILES-WEB-9KR0, MIXTILES-WEB-9P9D (Facebook contact autofill bridge)
const SOCIAL_IN_APP_BROWSER_INJECTED_FUNCTIONS = new Set([
  'sendDataToNative',
  'sendPageShowMessage',
  'processLargestContentfulPaintEvent',
  'setContactAutofillValuesFromBridge',
])

export function dropSocialInAppBrowserInjectedNoise(event) {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
  if (
    frames.some((f) => SOCIAL_IN_APP_BROWSER_INJECTED_FUNCTIONS.has(f.function))
  ) {
    return null
  }
  return event
}

const ABORT_ERROR_NAME = 'AbortError'

// Engine-specific messages (the spec fixes only the name). Matching the name alone
// would also drop play(), IndexedDB, media and ServiceWorker failures.
const NAVIGATION_ABORT_REASONS = new Set([
  ABORT_ERROR_NAME,
  'signal is aborted without reason',
  'BodyStreamBuffer was aborted',
  'Fetch is aborted',
  'The user aborted a request.',
  'The operation was aborted.',
])

// Sentry puts the name in `type` when the DOMException has a `stack`, and folds it
// into `value` as "<name>: <reason>" when it doesn't (@sentry/browser eventbuilder).
function getAbortRejectionReason(exception) {
  if (exception?.mechanism?.type !== 'onunhandledrejection') return null
  const value = exception.value ?? ''
  if (exception.type === ABORT_ERROR_NAME) return value
  const stacklessPrefix = `${ABORT_ERROR_NAME}: `
  return value.startsWith(stacklessPrefix)
    ? value.slice(stacklessPrefix.length)
    : null
}

export function dropNavigationAbortNoise(event) {
  // `values` runs oldest-cause first, so the rejected error — the only one with a
  // real mechanism — is last (@sentry/utils applyAggregateErrorsToEvent).
  const rejectedException = event.exception?.values?.at(-1)
  return NAVIGATION_ABORT_REASONS.has(
    getAbortRejectionReason(rejectedException)
  )
    ? null
    : event
}

export function dropKnownNoise(event) {
  if (dropSocialInAppBrowserInjectedNoise(event) === null) return null
  if (dropNavigationAbortNoise(event) === null) return null
  return event
}

export function redactStripeClientSecretsInEvent(event) {
  // sdkProcessingMetadata holds live SDK objects (scopes, timers) - never serialize it
  const { sdkProcessingMetadata, ...serializableEvent } = event
  let serializedEvent
  try {
    serializedEvent = JSON.stringify(serializableEvent)
  } catch {
    // Sentry drops the event when beforeSend/beforeSendTransaction throws
    return event
  }
  const redactedEvent = redactSecretsInText(serializedEvent)
  return redactedEvent === serializedEvent
    ? event
    : { ...JSON.parse(redactedEvent), sdkProcessingMetadata }
}

class SentryService {
  initSentry(keys) {
    if (window.KEYS.runtimeEnv !== 'local') {
      try {
        const defaultIntegrations = [
          Sentry.captureConsoleIntegration({ levels: ['error'] }),
          Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          }),
        ]

        Sentry.init({
          dsn: 'https://04d99f9858cf47bdb586902e3b11a797@o223238.ingest.us.sentry.io/1372017',
          environment: keys.runtimeEnv,
          release: keys.buildCommit,
          attachStacktrace: true,
          integrations: defaultIntegrations,
          tracesSampleRate: Number(keys.tracesSampleRate) || 0.1,
          initialScope: {
            extra: { 'Capture Level': 'React Router Client' },
            contexts: { app: {} },
          },
          beforeSend: (event) => {
            const keptEvent = dropKnownNoise(event)
            return keptEvent && redactStripeClientSecretsInEvent(keptEvent)
          },
          beforeSendTransaction: redactStripeClientSecretsInEvent,
        })
      } catch (error) {
        logger.error(
          'Error initiating Sentry.. will anyone ever see this??',
          error
        )
      }
    }
  }

  identifyUser(userId) {
    Sentry.setUser({ id: userId })
  }

  reportErrorManually(exception) {
    Sentry.captureException(exception)
  }
}

export const sentryService = new SentryService()
